import { createRouter, createWebHistory, h } from 'vue-router'
import { getUser, isLoggedIn } from '../auth'

// Containers
const TheContainer = () => import('@/containers/TheContainer')

// ── Public Pages ──
const Index        = () => import('@/views/Index')
const Home         = () => import('@/views/Home')
const About        = () => import('@/views/About')
const HowItWork    = () => import('@/views/HowItWork')
const ContactUs    = () => import('@/views/ContactUs')

// ── Auth ──
const LoginAccount       = () => import('@/views/LoginAccount')
const LoginType          = () => import('@/views/LoginType')
const SignUp             = () => import('@/views/SignUp')
const RegisterUser       = () => import('@/views/RegisterUser')
const CreateAccount      = () => import('@/views/CreateAccount')
const ResetPassword      = () => import('@/views/ResetPassword')
const VerificationPassword = () => import('@/views/VerificationPassword')
const SetNewPassword     = () => import('@/views/SetNewPassword')
const RegisterHomepage   = () => import('@/views/RegisterHomepage')
const RegisterApp        = () => import('@/views/RegisterApp')
const WhoAreYou          = () => import('@/views/WhoAreYou')
const CompleteProfile    = () => import('@/views/CompleteProfile')

// ── Player ──
const Dashboard        = () => import('@/views/Dashboard')
const Profile          = () => import('@/views/Profile')
const ProfileSetting   = () => import('@/views/ProfileSetting')
const PersonalInformation = () => import('@/views/PersonalInformation')
const Notify           = () => import('@/views/Notify')

// ── Match / Game ──
const MatchingOpponent = () => import('@/views/MatchingOpponent')
const MatchingPlayer   = () => import('@/views/MatchingPlayer')
const MatchGroup       = () => import('@/views/MatchGroup')
const AssignTeamGroup  = () => import('@/views/AssignTeamGroup')
const ViewParticipant  = () => import('@/views/ViewParticipant')
const ViewTeams        = () => import('@/views/ViewTeams')

// ── Tournament / Event ──
const FormTournament   = () => import('@/views/FormTournament')
const EventManagement  = () => import('@/views/EventManagement')
const CreateEvent      = () => import('@/views/CreateEvent')
const CustomizeEvent   = () => import('@/views/CustomizeEvent')
const Announcement     = () => import('@/views/Announcement')
const Category         = () => import('@/views/Category')

// ── Training / Course ──
const TrainingClass       = () => import('@/views/TrainingClass')
const RegisterGames       = () => import('@/views/RegisterGames')
const JoinTeam            = () => import('@/views/JoinTeam')
const ManageTeamMembers   = () => import('@/views/ManageTeamMembers')
const MyRegistrations     = () => import('@/views/MyRegistrations')
const Bootcamp            = () => import('@/views/Bootcamp')
const BootcampById     = () => import('@/views/BootcampById')
const EditBootcamp     = () => import('@/views/EditBootcamp')
const AllBootcamp      = () => import('@/views/AllBootcamp')
const AllCourses       = () => import('@/views/AllCourses')
const UploadCourse     = () => import('@/views/UploadCourse')
const ELearning        = () => import('@/views/ELearning')

// ── Membership / Payment ──
const Membership       = () => import('@/views/Membership')
const MembershipList   = () => import('@/views/MembershipList')
const ManageMembership = () => import('@/views/ManageMembership')
const Payment          = () => import('@/views/Payment')
const MyCart           = () => import('@/views/MyCart')
const Affiliate        = () => import('@/views/Affiliate')

// ── Admin ──
const AdminPanel       = () => import('@/views/AdminPanel')
const Settings         = () => import('@/views/Settings')

// ── Join via QR ──
const JoinChallenge    = () => import('@/views/JoinChallenge')

// ── Elo / Ranking ──
const NewGame             = () => import('@/views/NewGame')
const Ranking             = () => import('@/views/Ranking')
const RankingCalculator   = () => import('@/views/RankingCalculator')
const SubmitResult        = () => import('@/views/SubmitResult')
const PlayerProfile       = () => import('@/views/PlayerProfile')
const MyEvents            = () => import('@/views/MyEvents')
const EventLobby          = () => import('@/views/EventLobby')

// ── My Tournaments ──
const MyTournaments    = () => import('@/views/MyTournaments')

// ── Misc ──
const Gallery          = () => import('@/views/Gallery')
const Calendar         = () => import('@/views/Calendar')

// ── Community ──
const CommunityFeed      = () => import('@/views/community/CommunityFeed')
const CommunityUpload    = () => import('@/views/community/CommunityUpload')
const CommunityProfile   = () => import('@/views/community/CommunityProfile')
const CommunityModeration = () => import('@/views/community/CommunityModeration')

// ── Legal (public, linked from Google Play listing) ──
const PrivacyPolicy      = () => import('@/views/PrivacyPolicy')
const AccountDeletion    = () => import('@/views/AccountDeletion')

// ── CoreUI Template Pages (keep for reference) ──
const Page404 = () => import('@/views/pages/Page404')
const Page500 = () => import('@/views/pages/Page500')

const RouterView = { render: () => h('router-view') }

const router = createRouter({
  history: createWebHistory('/'),
  linkActiveClass: 'active',
  scrollBehavior: () => ({ top: 0 }),
  routes: [

    // ── Root ──
    { path: '/', name: 'Index', component: Index },

    // ── Auth routes (standalone, no sidebar) ──
    { path: '/LoginType',           name: 'LoginType',           component: LoginType },
    { path: '/LoginAccount',        name: 'LoginAccount',        component: LoginAccount },
    { path: '/SignUp',              name: 'SignUp',              component: SignUp },
    { path: '/RegisterUser',        name: 'RegisterUser',        component: RegisterUser },
    { path: '/CreateAccount',       name: 'CreateAccount',       component: CreateAccount },
    { path: '/ResetPassword',       name: 'ResetPassword',       component: ResetPassword },
    { path: '/VerificationPassword',name: 'VerificationPassword',component: VerificationPassword },
    { path: '/SetNewPassword',      name: 'SetNewPassword',      component: SetNewPassword },
    { path: '/RegisterApp',         name: 'RegisterApp',         component: RegisterApp },
    { path: '/RegisterHomepage',    name: 'RegisterHomepage',    component: RegisterHomepage },
    { path: '/WhoAreYou',           name: 'WhoAreYou',           component: WhoAreYou },
    { path: '/join',                name: 'JoinChallenge',       component: JoinChallenge },
    { path: '/CompleteProfile',     name: 'CompleteProfile',     component: CompleteProfile },

    // ── Public standalone pages ──
    { path: '/About',      name: 'About',      component: About },
    { path: '/HowItWork',  name: 'HowItWork',  component: HowItWork },
    { path: '/ContactUs',  name: 'ContactUs',  component: ContactUs },
    { path: '/Home',       name: 'Home',       component: Home },
    { path: '/TournamentPublicPage', name: 'TournamentPublicPage', component: () => import('@/views/TournamentPublicPage') },
    { path: '/TournamentShowcase', name: 'TournamentShowcase', component: () => import('@/views/TournamentShowcase') },
    { path: '/PrivacyPolicy',      name: 'PrivacyPolicy',      component: PrivacyPolicy },
    { path: '/AccountDeletion',    name: 'AccountDeletion',    component: AccountDeletion },

    // ── Community (immersive mobile-first feed) ──
    { path: '/Community',                 name: 'Community',        component: CommunityFeed },
    { path: '/Community/Post/:id',        name: 'CommunityPost',    component: CommunityFeed },
    { path: '/Community/Upload',          name: 'CommunityUpload',  component: CommunityUpload },
    { path: '/Community/User/:id',        name: 'CommunityProfile', component: CommunityProfile },

    // ── App pages inside TheContainer (with sidebar/navbar) ──
    {
      path: '/',
      component: TheContainer,
      children: [
        { path: 'Dashboard',          name: 'Dashboard',          component: Dashboard },
        { path: 'Profile',            name: 'Profile',            component: Profile },
        { path: 'ProfileSetting',     name: 'ProfileSetting',     component: ProfileSetting },
        { path: 'PersonalInformation',name: 'PersonalInformation',component: PersonalInformation },
        { path: 'Notify',             name: 'Notify',             component: Notify },

        { path: 'MatchingOpponent',   name: 'MatchingOpponent',   component: MatchingOpponent },
        { path: 'MatchingPlayer',     name: 'MatchingPlayer',     component: MatchingPlayer },
        { path: 'MatchGroup',         name: 'MatchGroup',         component: MatchGroup },
        { path: 'AssignTeamGroup',    name: 'AssignTeamGroup',    component: AssignTeamGroup },
        { path: 'ViewParticipant',    name: 'ViewParticipant',    component: ViewParticipant },
        { path: 'ViewTeams',          name: 'ViewTeams',          component: ViewTeams },

        { path: 'TournamentDashboard',   name: 'TournamentDashboard',   component: () => import('@/views/TournamentDashboard') },
        { path: 'TournamentDiscovery',   name: 'TournamentDiscovery',   component: () => import('@/views/TournamentDiscovery') },
        { path: 'FormTournament',     name: 'FormTournament',     component: FormTournament },
        { path: 'TournamentGroupManagement', name: 'TournamentGroupManagement', component: () => import('@/views/TournamentGroupManagement') },
        { path: 'TournamentMatchDisplay', name: 'TournamentMatchDisplay', component: () => import('@/views/TournamentMatchDisplay') },
        { path: 'TournamentStandings', name: 'TournamentStandings', component: () => import('@/views/TournamentStandings') },
        { path: 'KnockoutBracket', name: 'KnockoutBracket', component: () => import('@/views/KnockoutBracket') },
        { path: 'EventManagement',    name: 'EventManagement',    component: EventManagement },
        { path: 'CreateEvent',        name: 'CreateEvent',        component: CreateEvent },
        { path: 'CustomizeEvent',     name: 'CustomizeEvent',     component: CustomizeEvent },
        { path: 'Announcement',       name: 'Announcement',       component: Announcement },
        { path: 'Category',           name: 'Category',           component: Category },

        { path: 'TrainingClass',       name: 'TrainingClass',       component: TrainingClass },
        { path: 'RegisterGames',       name: 'RegisterGames',       component: RegisterGames },
        { path: 'JoinTeam',            name: 'JoinTeam',            component: JoinTeam },
        { path: 'ManageTeamMembers',   name: 'ManageTeamMembers',   component: ManageTeamMembers },
        { path: 'MyRegistrations',     name: 'MyRegistrations',     component: MyRegistrations },
        { path: 'Bootcamp',            name: 'Bootcamp',            component: Bootcamp },
        { path: 'BootcampById',       name: 'BootcampById',       component: BootcampById },
        { path: 'EditBootcamp',       name: 'EditBootcamp',       component: EditBootcamp },
        { path: 'AllBootcamp',        name: 'AllBootcamp',        component: AllBootcamp },
        { path: 'AllCourses',         name: 'AllCourses',         component: AllCourses },
        { path: 'UploadCourse',       name: 'UploadCourse',       component: UploadCourse },
        { path: 'ELearning',          name: 'ELearning',          component: ELearning },

        { path: 'Membership',         name: 'Membership',         component: Membership },
        { path: 'MembershipList',     name: 'MembershipList',     component: MembershipList },
        { path: 'ManageMembership',   name: 'ManageMembership',   component: ManageMembership },
        { path: 'Payment',            name: 'Payment',            component: Payment },
        { path: 'MyCart',             name: 'MyCart',             component: MyCart },
        { path: 'Affiliate',          name: 'Affiliate',          component: Affiliate },

        { path: 'AdminPanel',         name: 'AdminPanel',         component: AdminPanel },
        { path: 'Settings',           name: 'Settings',           component: Settings },
        { path: 'NewGame',            name: 'NewGame',            component: NewGame },
        { path: 'Ranking',            name: 'Ranking',            component: Ranking },
        { path: 'RankingCalculator',  name: 'RankingCalculator',  component: RankingCalculator },
        { path: 'SubmitResult',       name: 'SubmitResult',       component: SubmitResult },
        { path: 'PlayerProfile',      name: 'PlayerProfile',      component: PlayerProfile },
        { path: 'MyEvents',           name: 'MyEvents',           component: MyEvents },
        { path: 'EventLobby',         name: 'EventLobby',         component: EventLobby },

        { path: 'MyTournaments',      name: 'MyTournaments',      component: MyTournaments },
        { path: 'Gallery',            name: 'Gallery',            component: Gallery },
        { path: 'Calendar',           name: 'Calendar',           component: Calendar },
        { path: 'CommunityModeration', name: 'CommunityModeration', component: CommunityModeration },
        { path: 'More',               name: 'More',               component: () => import('@/views/More.vue') },
      ]
    },

    // ── Error pages ──
    { path: '/404',  name: 'Page404', component: Page404 },
    { path: '/500',  name: 'Page500', component: Page500 },
    { path: '/:pathMatch(.*)*', redirect: '/404' },
  ]
})

const PUBLIC_ROUTES = ['Index', 'LoginType', 'LoginAccount', 'SignUp', 'RegisterUser',
  'CreateAccount', 'ResetPassword', 'VerificationPassword', 'SetNewPassword',
  'RegisterApp', 'RegisterHomepage', 'WhoAreYou', 'About', 'HowItWork',
  'ContactUs', 'Home', 'Page404', 'Page500', 'JoinChallenge', 'CompleteProfile',
  'TournamentPublicPage', 'TournamentShowcase',
  'Community', 'CommunityPost', 'CommunityProfile',
  'PrivacyPolicy', 'AccountDeletion']

router.beforeEach((to, from, next) => {
  if (PUBLIC_ROUTES.includes(to.name)) return next()

  if (!isLoggedIn()) return next('/LoginAccount')

  // Skip profile-complete check — let users navigate freely
  // const user = getUser()
  // if (!user?.profileComplete && to.name !== 'CompleteProfile') {
  //   return next('/CompleteProfile')
  // }

  next()
})

export default router
